import { useQuery } from '@tanstack/react-query';
import { differenceInCalendarDays } from 'date-fns';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { getLocal } from 'api/adminApi';
import {
  PermissionFormData,
  useRegisterPermission,
} from 'api/lotoManagementApi';
import { getLotoConclusionList } from 'api/lotoStatusApi';
import { localKeys, lotoConclusionKeys } from 'api/quires/queryKeys';
import { LotoOrderSearchRequest } from 'api/searchRequest/LotoOrderSearchRequest';
import LotoPermissionModal from 'components/loto-management/modal/LotoPermissionModal';
import Pagination from 'components/widget/Pagination';
import { Loto } from 'model/Loto';
import { useRecoilValue } from 'recoil';
import { userInfoState } from 'recoil/user/atom';
import { YN_TYPE } from 'types/enumType';
import {
  containsSpecialCharAndSql,
  getAcceptInfoByUserInfo,
  isSuperAdmin,
} from 'util/commonUtil';
import { decryptId, decryptString } from 'util/encryptUtil';

import LotoSearchBox from '../searchbox/LotoSearchBox';
import LotoConclusionTable from './LotoConclusionTable';

export default function LotoConclusionPopup() {
  const { t } = useTranslation();
  const userInfo = useRecoilValue(userInfoState);
  const [queryParams] = useSearchParams();

  const role = decryptString(userInfo.role);
  const isSuper = isSuperAdmin(role, decryptId(userInfo.factoryId));
  const [reqNo, setReqNo] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);

  const [searchRequest, setSearchRequest] = useState<LotoOrderSearchRequest>(
    () => {
      const req = new LotoOrderSearchRequest(
        1,
        20,
        userInfo.local,
        decryptId(userInfo.factoryId ?? ''),
        true,
        YN_TYPE.Y,
      );
      return req;
    },
  );

  const { data: localData } = useQuery(localKeys.all, getLocal, {
    enabled: isSuper,
  });

  const { data, isLoading, refetch } = useQuery(
    lotoConclusionKeys.list(searchRequest),
    () => getLotoConclusionList(searchRequest),
    {
      cacheTime: 1000,
    },
  );

  // const url = new URL('./worker.js', import.meta.url);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // if (event.origin !== window.location.origin) return; // Security check
      const { REQ_NO } = event.data;
      if (!REQ_NO) {
        return;
      }

      setReqNo(REQ_NO);
    };

    window.addEventListener('message', handleMessage);
    // window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      setReqNo('');
    };
  }, []);

  useEffect(() => {
    if (!localData || !localData.list) {
      return;
    }

    const local = userInfo.local ? userInfo.local : localData.list[0].id ?? '';
    if (local) {
      setSearchRequest((prev) => ({
        ...prev,
        localId: local.toString(),
      }));
    }
  }, [localData]);

  const onChangeLocal = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    setSearchRequest((prev) => ({ ...prev, localId: value, factoryId: '' }));
  };

  const handleSearchSubmit = (formData: LotoOrderSearchRequest) => {
    if (
      formData.startDate &&
      formData.endDate &&
      differenceInCalendarDays(
        new Date(formData.startDate),
        new Date(formData.endDate),
      ) > 0
    ) {
      alert(t('conclusion.bigStartDate'));

      return;
    }

    if (
      containsSpecialCharAndSql(formData.equipName) ||
      containsSpecialCharAndSql(formData.searchKey)
    ) {
      alert(t('conclusion.notIncludeSQL'));
      return;
    }

    setSearchRequest((prev) => ({
      ...prev,
      ...formData,
    }));
    setTimeout(() => refetch());
  };
  const onChangePage = (page: number) => {
    setSearchRequest((prev) => ({ ...prev, page }));
  };

  const onSelectRow = (loto: Loto) => {
    if (!window.opener) {
      alert(t('validation.notParent'));
      return;
    }

    if (
      !window.confirm(
        `${t('validation.confirmOrder')}\n[${loto.orderName}]-(${
          loto.orderNo
        })`,
      )
    ) {
      return;
    }

    // console.log({
    //   REQ_NO: queryParams.get('request_no'),
    //   LOTO_NO: loto.orderNo,
    // });
    window.opener.postMessage(
      { REQ_NO: queryParams.get('request_no'), LOTO_NO: loto.orderNo },
      '*',
    );
  };

  const [confirmIsOpen, setConfirmIsOpen] = useState(false);

  const handleConfirmModalOpen = (event: React.MouseEvent, id: number) => {};

  const onOpenPermissionModal = () => {
    setIsOpen(true);
  };

  const onClosePermissionModal = () => {
    setIsOpen(false);
  };

  const registerPermissionMutate = useRegisterPermission();
  const handleRegisterSubmit = (formData: PermissionFormData) => {
    registerPermissionMutate.mutate(
      { ...formData, ...getAcceptInfoByUserInfo(userInfo) },
      {
        onSuccess: (resp) => {
          if (resp.code === 200) {
            if (!window.opener) {
              alert(t('validation.notParent'));
            } else {
              window.opener.postMessage(
                {
                  REQ_NO: queryParams.get('request_no'),
                  LOTO_NO: resp.requestNo,
                },
                '*',
              );
            }

            onClosePermissionModal();
            alert(t('orderManagement.orderRegistComplete'));
          } else {
            alert(resp.message);
          }
        },
      },
    );
  };

  return (
    <>
      <div id={'contents'} className={'popup'}>
        <div className={'title'}>
          <p>{t('conclusion.conclusionStatus')}</p>
          {isSuper && (
            <div className={'select-wrap'}>
              <select
                className={'factoryCode-wrap'}
                value={userInfo?.local}
                onChange={onChangeLocal}
              >
                {localData?.list?.map((local) => (
                  <option value={local.id} key={`${local.id} ${local.code}`}>
                    {local.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <LotoSearchBox
          searchRequest={searchRequest}
          isLoading={isLoading}
          onSubmit={handleSearchSubmit}
        />
        <div className={'cont-wrap'}>
          <div className={'cont-top'}>
            <div className={'subtit'}>
              <p>{t('conclusion.searchResult')}</p>
              <p className={'count'}>{`${data?.totalCount ?? 0} ${t(
                'conclusion.thing',
              )}`}</p>
            </div>
            <div className={'right-wrap'}>
              <button
                type={'button'}
                className={'orange_white_btn mg5r'}
                onClick={onOpenPermissionModal}
              >
                {'등록'}
              </button>
            </div>
          </div>
          <div className={'cont'}>
            <div className={'tbl-wrap'}>
              <LotoConclusionTable
                conclusionList={data?.list ?? []}
                page={searchRequest.page}
                count={searchRequest.count}
                onClick={onSelectRow}
                confirmModalOpen={handleConfirmModalOpen}
                oldLotoYn={YN_TYPE.N}
                isPopup
              />
            </div>
            <Pagination
              page={searchRequest.page}
              totalCount={data?.totalCount ?? 0}
              count={searchRequest.count}
              onPageClick={onChangePage}
            />
          </div>
        </div>
      </div>
      {isOpen && (
        <LotoPermissionModal
          isOpen={isOpen}
          onClose={onClosePermissionModal}
          onSubmit={handleRegisterSubmit}
        />
      )}
    </>
  );
}
